import { useMemo, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useGallery,
  useSlotGroup,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArchive,
  faBanSmoking,
  faBasketball,
  faBath,
  faBed,
  faBellConcierge,
  faBicycle,
  faBolt,
  faBookOpen,
  faBox,
  faBriefcase,
  faBuilding,
  faBuildingUser,
  faCalendarDays,
  faCar,
  faCarSide,
  faChess,
  faClipboardList,
  faComputer,
  faCouch,
  faCreditCard,
  faDoorOpen,
  faDroplet,
  faDumbbell,
  faElevator,
  faEnvelope,
  faFan,
  faFileContract,
  faFire,
  faFireBurner,
  faGamepad,
  faGolfBallTee,
  faHandshake,
  faHeartPulse,
  faHotTubPerson,
  faHouse,
  faIceCream,
  faKey,
  faKitchenSet,
  faLanguage,
  faLeaf,
  faMartiniGlass,
  faMaximize,
  faMedal,
  faMoneyBillWave,
  faMountainSun,
  faMugHot,
  faPaintRoller,
  faPaw,
  faPersonSwimming,
  faPersonWalking,
  faPhone,
  faPrint,
  faRecycle,
  faRug,
  faRulerVertical,
  faSeedling,
  faShieldHalved,
  faShirt,
  faShower,
  faSink,
  faSnowflake,
  faSpa,
  faSquareParking,
  faStar,
  faStore,
  faTable,
  faTrashCan,
  faTree,
  faTv,
  faUmbrellaBeach,
  faUserShield,
  faUsers,
  faUtensils,
  faVolleyball,
  faWarehouse,
  faWater,
  faWeightHanging,
  faWheelchair,
  faWifi,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { excludeOtherPhotos, otherPhotoUrls } from '../utils/galleryCategory';
interface Amenity {
  title?: string | null;
  amenity_icon?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
}

// ============================================================================
// Icon mapping (adapted from the Vantage theme's PropertyOverview)
//
// The amenity feed rarely ships its own `amenity_icon`, so we derive a
// FontAwesome icon from keywords in each amenity's title. Rules are ordered
// most-specific → least-specific and the first match wins; unknown titles fall
// back to faStar.
// ============================================================================

// Most icons are FontAwesome, but a few amenities read better with a bespoke
// inline drawing. The 'seesaw' sentinel is rendered as a hand-drawn
// teeter-totter (see SeesawIcon) instead of going through FontAwesome.
type AmenityIcon = IconDefinition | 'seesaw';
type IconRule = { keywords: string[]; icon: AmenityIcon };

const AMENITY_ICON_RULES: IconRule[] = [
  // Payments & online services
  { keywords: ['online payment', 'online rent', 'rent payment', 'pay rent'], icon: faCreditCard },
  { keywords: ['online service', 'service request'], icon: faClipboardList },
  { keywords: ['quick connect', 'high-speed internet', 'high speed internet', 'free internet'], icon: faWifi },

  // Maintenance & staff
  { keywords: ['emergency maintenance', '24-hour maintenance', '24 hour maintenance'], icon: faWrench },
  { keywords: ['on-site maintenance', 'onsite maintenance', 'maintenance'], icon: faWrench },
  { keywords: ['on-site management', 'onsite management', 'property management', 'property manager', 'community manager', 'resident manager', 'on-site manager', 'onsite manager', 'manager', 'management'], icon: faBuildingUser },
  { keywords: ['courtesy patrol', 'security patrol'], icon: faUserShield },
  { keywords: ['bilingual'], icon: faLanguage },
  { keywords: ['concierge'], icon: faBellConcierge },
  { keywords: ['leasing office', 'leasing center', 'leasing team', 'leasing'], icon: faBuildingUser },

  // Programs & discounts
  { keywords: ['renters insurance', 'renter insurance'], icon: faFileContract },
  { keywords: ['flexible lease', 'flexible lease term', 'lease term', 'short term lease', 'month to month'], icon: faFileContract },
  { keywords: ['employer discount', 'preferred employer'], icon: faHandshake },
  { keywords: ['military discount', 'active duty military', 'active duty'], icon: faMedal },
  { keywords: ['planned social activities', 'planned social', 'social activities', 'social activity', 'social calendar', 'resident event', 'community event'], icon: faCalendarDays },

  // Community spaces
  { keywords: ['study space', 'study room', 'study lounge', 'study hall', 'study area', 'study'], icon: faBookOpen },
  { keywords: ['business center', 'conference room', 'conference', 'co-work', 'cowork', 'meeting room'], icon: faBriefcase },
  { keywords: ['mail center', 'mailroom', 'mail room', 'package room', 'package', 'mail'], icon: faEnvelope },
  { keywords: ['marketplace', 'market'], icon: faStore },
  { keywords: ['coffee shop', 'coffee station', 'coffee bar', 'cafe', 'coffee'], icon: faMugHot },
  { keywords: ['game room', 'arcade'], icon: faGamepad },
  { keywords: ['resident lounge', 'sky lounge', 'penthouse lounge'], icon: faCouch },
  { keywords: ['poolside clubhouse', 'clubhouse'], icon: faHouse },
  // Pet play spaces must beat the generic "play area" → playground rule below,
  // so this more-specific pet match is listed first.
  { keywords: ['pet play', 'pet park', 'bark park', 'dog play'], icon: faPaw },
  { keywords: ['playground', 'play area', 'tot lot', 'jungle gym'], icon: 'seesaw' },
  { keywords: ['grilling', 'grill', 'bbq', 'barbecue'], icon: faFireBurner },
  { keywords: ['lounge', 'bar'], icon: faMartiniGlass },

  // Recreation & wellness
  { keywords: ['pet center', 'pet resort', 'pet spa', 'dog wash', 'pet wash', 'pet groom', 'grooming', 'veterinary', 'day boarding', 'pet boarding'], icon: faPaw },
  { keywords: ['hot tub'], icon: faHotTubPerson },
  { keywords: ['resort-inspired pool', 'resort inspired pool', 'resort style pool', 'resort pool', 'swimming pool', 'rooftop pool'], icon: faPersonSwimming },
  { keywords: ['pool', 'pond'], icon: faWater },
  { keywords: ['cardio machine', 'cardio equipment', 'cardio'], icon: faHeartPulse },
  { keywords: ['free weight', 'free weights'], icon: faWeightHanging },
  { keywords: ['weight machine', 'weight machines'], icon: faDumbbell },
  { keywords: ['fitness center', 'fitness', 'gym', 'workout'], icon: faDumbbell },
  { keywords: ['spa', 'yoga', 'pilates', 'sauna'], icon: faSpa },
  { keywords: ['golf'], icon: faGolfBallTee },
  { keywords: ['volleyball'], icon: faVolleyball },
  { keywords: ['basketball'], icon: faBasketball },
  { keywords: ['lawn game', 'lawn games', 'bocce', 'cornhole'], icon: faChess },
  { keywords: ['recycling', 'recycle'], icon: faRecycle },
  { keywords: ['bike', 'bicycle', 'cycling'], icon: faBicycle },

  // Grounds & outdoor
  { keywords: ['walking trail', 'walking trails', 'walking path', 'hiking trail', 'hiking trails'], icon: faPersonWalking },
  { keywords: ['car wash'], icon: faCarSide },
  { keywords: ['nature view', 'nature views', 'scenic view', 'scenic views'], icon: faMountainSun },
  { keywords: ['landscaping', 'landscaped', 'landscape', 'grounds', 'garden', 'courtyard'], icon: faTree },
  { keywords: ['outdoor', 'patio', 'terrace', 'balcony', 'deck'], icon: faUmbrellaBeach },
  { keywords: ['roof deck', 'rooftop'], icon: faBuilding },

  // Pets & policies
  { keywords: ['dog park', 'pet play'], icon: faPaw },
  { keywords: ['pet', 'dog', 'cat', 'pets welcome'], icon: faPaw },
  { keywords: ['smoke-free', 'smoke free', 'non-smoking', 'nonsmoking'], icon: faBanSmoking },

  // Parking & access
  { keywords: ['garage parking', 'assigned garage', 'attached garage', 'garage'], icon: faWarehouse },
  { keywords: ['covered parking', 'parking'], icon: faSquareParking },
  // Must sit AFTER the garage rule: 'garbage' is only one edit from 'garage', so
  // a garage photo/amenity would otherwise fuzzy-match here and be mislabeled as
  // trash. Letting the garage rule claim it first avoids that collision.
  { keywords: ['valet trash', 'trash pickup', 'trash pick up', 'trash service', 'trash removal', 'doorstep trash', 'waste removal', 'waste pickup', 'garbage', 'rubbish', 'trash', 'waste'], icon: faTrashCan },
  { keywords: ['ev charging', 'electric car', 'charging station', 'car charging'], icon: faBolt },
  { keywords: ['keyless', 'key fob', 'access controlled', 'access control', 'controlled access', 'gated access'], icon: faKey },
  { keywords: ['direct access', 'dual entry', 'private entry', 'entry'], icon: faDoorOpen },
  { keywords: ['elevator'], icon: faElevator },

  // In-unit features
  { keywords: ['open concept layout', 'open concept', 'open layout', 'concept layout'], icon: faMaximize },
  { keywords: ['vinyl flooring', 'vinyl floor', 'vinyl plank', 'luxury vinyl'], icon: faRug },
  { keywords: ['hardwood', 'wood style flooring', 'wood flooring', 'wood floor', 'tile floor', 'flooring'], icon: faRug },
  { keywords: ['air conditioning', 'a/c', 'a.c.', 'central air', 'central ac', 'climate control'], icon: faSnowflake },
  { keywords: ['fireplace', 'fire place', 'wood burning'], icon: faFire },
  { keywords: ['ceiling fan', 'ceiling fans'], icon: faFan },
  { keywords: ['washer', 'dryer', 'laundry', 'in-home wash'], icon: faShirt },
  { keywords: ['dishwasher'], icon: faUtensils },
  { keywords: ['microwave'], icon: faKitchenSet },
  { keywords: ['ice maker'], icon: faIceCream },
  { keywords: ['kitchen island'], icon: faKitchenSet },
  { keywords: ['glass top range', 'glass cooktop', 'cooktop', 'range', 'stove', 'oven'], icon: faFireBurner },
  { keywords: ['refrigerator', 'fridge'], icon: faKitchenSet },
  { keywords: ['cabinetry', 'cabinet'], icon: faKitchenSet },
  { keywords: ['backsplash'], icon: faKitchenSet },
  { keywords: ['energy efficient', 'energy star'], icon: faLeaf },
  { keywords: ['stainless', 'appliance package', 'appliances', 'appliance'], icon: faKitchenSet },
  { keywords: ['granite', 'quartz', 'countertop', 'counter top'], icon: faTable },
  { keywords: ['walk-in closet', 'walk in closet', 'master closet', 'closet'], icon: faArchive },
  { keywords: ['pantry'], icon: faBox },
  { keywords: ['storage closet', 'storage'], icon: faBox },
  { keywords: ['double vanities', 'dual vanities', 'dual vanity', 'vanity'], icon: faSink },
  { keywords: ['sprinkler'], icon: faDroplet },
  { keywords: ['foot ceiling', 'high ceiling', 'vaulted ceiling', '9 foot', '9 ft'], icon: faRulerVertical },
  { keywords: ['crown molding', 'crown moulding'], icon: faPaintRoller },
  { keywords: ['tile shower', 'shower', 'glass enclosure'], icon: faShower },
  { keywords: ['bath', 'bathtub'], icon: faBath },
  { keywords: ['bedroom', 'bed'], icon: faBed },
  { keywords: ['smart home', 'wired for technology', 'technology', 'wifi', 'wi fi', 'internet', 'fiber', 'broadband'], icon: faWifi },
  { keywords: ['cable', 'tv'], icon: faTv },

  // General fallbacks
  { keywords: ['security', 'gated', 'controlled access'], icon: faShieldHalved },
  { keywords: ['wheelchair', 'accessible', 'ada'], icon: faWheelchair },
  { keywords: ['printing', 'print service', 'print', 'copier', 'copy center'], icon: faPrint },
  { keywords: ['phone', 'call'], icon: faPhone },
  { keywords: ['payment', 'pay'], icon: faMoneyBillWave },
  { keywords: ['computer', 'cyber'], icon: faComputer },
  { keywords: ['car', 'valet'], icon: faCar },
  { keywords: ['event', 'entertainment'], icon: faUsers },
  { keywords: ['green', 'sustainable', 'eco', 'solar'], icon: faLeaf },
  { keywords: ['modern', 'industrial', 'decor', 'design'], icon: faBuilding },
  { keywords: ['plant', 'sprout', 'landscaping'], icon: faSeedling },
];

function normalizeAmenityText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[*]/g, '')
    .replace(/[‑–—/&-]/g, ' ')
    .replace(/\bcloseets?\b/g, 'closet')
    .replace(/\s+/g, ' ')
    .trim();
}

// Levenshtein edit distance, bailing out early once we exceed `max` so the
// hot loop over every keyword stays cheap.
function boundedLevenshtein(a: string, b: string, max: number): number {
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > max) return max + 1;
  if (m === 0) return n;
  if (n === 0) return m;

  let prev = new Array<number>(n + 1);
  let curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ai = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j++) {
      const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }

  return prev[n];
}

// How many typos/spelling differences we tolerate in a single word, scaled by
// its length. Short words must match exactly so that distinct amenities don't
// collide (e.g. "range"/"orange", "garage"/"garbage", "pool"/"pond"); only
// longer, more distinctive words earn fuzzy slack.
function fuzzyToleranceFor(word: string): number {
  const len = word.length;
  if (len >= 10) return 2;
  if (len >= 7) return 1;
  return 0;
}

// Does a keyword word appear among the amenity's words? Accepts common plural
// forms (-s/-es and -y → -ies) and, for longer words, a small length-scaled
// edit distance so variant spellings/typos still land on the right icon.
function amenityWordAppears(haystackWords: string[], keyword: string): boolean {
  const variants = new Set([keyword, `${keyword}s`, `${keyword}es`]);
  if (keyword.endsWith('y')) variants.add(`${keyword.slice(0, -1)}ies`);
  for (const word of haystackWords) {
    if (variants.has(word)) return true;
  }

  const tolerance = fuzzyToleranceFor(keyword);
  if (tolerance === 0) return false;
  return haystackWords.some(
    (word) => boundedLevenshtein(word, keyword, tolerance) <= tolerance,
  );
}

function amenityKeywordMatches(haystack: string, keyword: string): boolean {
  const normalized = normalizeAmenityText(keyword);
  if (!normalized) return false;

  const words = normalized.split(' ');

  // 1. High-confidence pass: the keyword's words appear adjacently and in order
  //    (with a trailing plural allowed on the final word).
  const prefix = words
    .slice(0, -1)
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('\\s+');
  const lastEscaped = words[words.length - 1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = prefix
    ? `\\b${prefix}\\s+${lastEscaped}(?:s|es)?\\b`
    : `\\b${lastEscaped}(?:s|es)?\\b`;
  if (new RegExp(pattern, 'i').test(haystack)) return true;

  // 2. Fuzzy pass: every significant word of the keyword shows up somewhere in
  //    the amenity text — order-independent, gaps allowed, plurals and small
  //    spelling differences tolerated. This is what lets a single rule cover the
  //    many ways different properties word the same amenity (e.g. "Resident
  //    Business & Coworking Center" still matches the "business center" rule).
  const significant = words.filter((word) => word.length > 2);
  if (significant.length === 0) return false;

  const haystackWords = haystack.split(' ').filter(Boolean);
  return significant.every((word) => amenityWordAppears(haystackWords, word));
}

function resolveAmenityIcon(title: string, iconHint?: string | null): AmenityIcon {
  const haystack = normalizeAmenityText(`${title} ${iconHint ?? ''}`);

  for (const rule of AMENITY_ICON_RULES) {
    if (rule.keywords.some((keyword) => amenityKeywordMatches(haystack, keyword))) {
      return rule.icon;
    }
  }

  return faStar;
}

// ============================================================================
// Image matching
//
// Amenity feeds rarely ship their own photo, so each card's back image is drawn
// from the property photo gallery. Rather than a naive "gallery[i]" wraparound
// (which drifts the wrong photo onto the wrong amenity), we score every gallery
// photo against every amenity and assign strongest-match-first. Assignment is a
// pure function of the data, so a given photo always lands on the same amenity
// instead of "moving around" between renders/generations.
// ============================================================================

// Generic filler words that shouldn't count as a topical match — otherwise a
// photo whose alt text merely says "community" would match half the amenities.
const IMAGE_STOP_WORDS = new Set([
  'the', 'and', 'with', 'for', 'our', 'your', 'from', 'this', 'that', 'all',
  'throughout', 'available', 'community', 'apartment', 'apartments', 'resident',
  'residents', 'onsite', 'site', 'service', 'services', 'access', 'area',
  'areas', 'space', 'spaces', 'room', 'rooms', 'center', 'centre', 'free',
  'new', 'photo', 'image', 'pic', 'jpg', 'jpeg', 'png', 'scaled', 'opt',
]);

// Pull human-meaningful words out of an image URL's filename, e.g.
// ".../pool-pic-7575ad13-1920w.jpg" → "pool pic". Digits/hashes/size suffixes
// are dropped so only real words survive.
function filenameWords(url: string): string {
  const path = url.split('?')[0];
  const file = path.slice(path.lastIndexOf('/') + 1).replace(/\.[a-z0-9]+$/i, '');
  return file.replace(/[^a-zA-Z]+/g, ' ');
}

interface Assigned {
  image: string;
  alt: string;
}

// Choose a distinct gallery photo for each amenity title. `pinned[i]` is an
// already-set (generated or hand-edited) image URL that must be kept as-is; the
// rest are matched by keyword/icon overlap, then any leftover cards fall back to
// the next unused photo so a card is never blank.
function assignPhotos(
  titles: string[],
  gallery: GalleryPhoto[],
  pinned: (string | undefined)[],
): Assigned[] {
  const result: Assigned[] = titles.map(() => ({ image: '', alt: '' }));

  const photos = gallery
    .map((p) => {
      const url = (p.image || p.original_url || '').toString();
      if (!url) return null;
      const text = normalizeAmenityText(
        [p.title, p.alt_text, p.description, filenameWords(url)]
          .filter(Boolean)
          .join(' '),
      );
      return {
        url,
        alt: (p.alt_text || p.title || '').toString(),
        words: text.split(' ').filter(Boolean),
        icon: resolveAmenityIcon(text),
      };
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);

  if (photos.length === 0) return result;

  const usedPhoto = new Set<number>();
  const assignedAmenity = new Set<number>();
  const urlToIndex = new Map<string, number>();
  photos.forEach((p, idx) => {
    if (!urlToIndex.has(p.url)) urlToIndex.set(p.url, idx);
  });

  // 1. Honour pinned (generated/edited) images and reserve their photos.
  pinned.forEach((url, i) => {
    if (!url) return;
    assignedAmenity.add(i);
    const idx = urlToIndex.get(url);
    result[i] = { image: url, alt: idx != null ? photos[idx].alt : '' };
    if (idx != null) usedPhoto.add(idx);
  });

  const isRealIcon = (icon: AmenityIcon) => icon !== faStar && icon !== 'seesaw';

  const amenities = titles.map((t) => ({
    icon: resolveAmenityIcon(t),
    words: normalizeAmenityText(t)
      .split(' ')
      .filter((w) => w.length > 2 && !IMAGE_STOP_WORDS.has(w)),
  }));

  // 2. Score every (amenity, photo) pair; assign globally strongest first so a
  //    standout match (e.g. the basketball photo → the basketball amenity) wins
  //    regardless of card order. Ties broken by index for deterministic output.
  const candidates: { i: number; p: number; score: number }[] = [];
  for (let i = 0; i < amenities.length; i++) {
    if (assignedAmenity.has(i)) continue;
    const a = amenities[i];
    for (let p = 0; p < photos.length; p++) {
      let overlap = 0;
      for (const w of a.words) {
        if (amenityWordAppears(photos[p].words, w)) overlap++;
      }
      let score = overlap;
      if (isRealIcon(a.icon) && a.icon === photos[p].icon) score += 3;
      if (score > 0) candidates.push({ i, p, score });
    }
  }
  candidates.sort((x, y) => y.score - x.score || x.i - y.i || x.p - y.p);
  for (const c of candidates) {
    if (assignedAmenity.has(c.i) || usedPhoto.has(c.p)) continue;
    result[c.i] = { image: photos[c.p].url, alt: photos[c.p].alt };
    assignedAmenity.add(c.i);
    usedPhoto.add(c.p);
  }

  // 3. Fill any still-unmatched amenity (e.g. "on-site management", "valet
  //    trash" — real amenities that no photo actually depicts) with a leftover
  //    photo. Prefer generic photos (no identifiable subject → faStar) so we
  //    don't stick, say, a garage photo on a trash-service card; a specific
  //    leftover is only used once the generic ones run out. Order is stable, so
  //    the fallback never drifts between renders.
  const isGeneric = (icon: AmenityIcon) => icon === faStar;
  const fillerQueue = photos
    .map((_, idx) => idx)
    .filter((idx) => !usedPhoto.has(idx))
    .sort((a, b) => Number(isGeneric(photos[b].icon)) - Number(isGeneric(photos[a].icon)) || a - b);
  let fq = 0;
  for (let i = 0; i < result.length; i++) {
    if (assignedAmenity.has(i)) continue;
    let chosen: number;
    if (fq < fillerQueue.length) {
      chosen = fillerQueue[fq++];
      usedPhoto.add(chosen);
    } else {
      chosen = i % photos.length; // more cards than photos: wrap as a last resort
    }
    result[i] = { image: photos[chosen].url, alt: photos[chosen].alt };
    assignedAmenity.add(i);
  }

  return result;
}

// ============================================================================
// Featured amenity cards
//
// Each card is an editable/generator-fillable slot group: it picks one standout
// amenity (title) and a fitting photo from the property gallery (image + alt).
// The generator suggests the community's top 8 standouts and a best-effort photo
// match — it never leaves the photo blank, falling back to the closest
// community/lifestyle image when an amenity has no obvious match. Until
// generation runs, each card defaults to a sequential amenity + gallery photo so
// the section is never empty and previews well on a company theme.
// ============================================================================

const MAX_CARDS = 8;

const card0Def: SlotGroupDefinition = {
  label: 'Featured Amenity 1',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[0].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 0,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

const card1Def: SlotGroupDefinition = {
  label: 'Featured Amenity 2',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[1].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 1,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Featured Amenity 3',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[2].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 2,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Featured Amenity 4',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[3].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 3,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

const card4Def: SlotGroupDefinition = {
  label: 'Featured Amenity 5',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[4].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 4,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

const card5Def: SlotGroupDefinition = {
  label: 'Featured Amenity 6',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[5].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 5,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

const card6Def: SlotGroupDefinition = {
  label: 'Featured Amenity 7',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[6].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 6,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

const card7Def: SlotGroupDefinition = {
  label: 'Featured Amenity 8',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[7].title', defaultValue: 'Amenity' },
    image: { label: 'Photo', type: 'image', defaultField: 'photo_gallery[7].image' },
    alt: { label: 'Photo Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text', defaultValue: 'Amenity' },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 7,
    fieldMappings: { title: 'title', image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt:
      'Select one standout amenity for this community and pair it with a fitting photo from the property photo gallery. Never leave the photo blank — not every amenity has an obvious matching photo, so make a best-effort match, and when nothing clearly fits choose the closest community or lifestyle image from the gallery.',
    context:
      'One of eight featured amenity cards on the Amenities page. Together the cards are the community\'s top 8 standout amenities; each card must be a different amenity, and each photo should suit its amenity.',
  },
};

interface CardView {
  title: string;
  icon: AmenityIcon;
  image: string;
  alt: string;
}

// Hand-drawn teeter-totter for playgrounds: a ground line, a diagonal beam
// balanced on a center fulcrum, and a seat at each end. Sized/colored like the
// FontAwesome icons via the shared "amenity-card-fa" class + inherited color.
function SeesawIcon(): ReactNode {
  return (
    <svg
      className="amenity-card-fa"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="3" y1="20" x2="21" y2="20" />
      <line x1="4.5" y1="15.5" x2="19.5" y2="9.5" />
      <path d="M12 12.5 10.4 20h3.2z" fill="currentColor" stroke="none" />
      <circle cx="5.2" cy="15" r="1.4" fill="currentColor" />
      <circle cx="18.8" cy="10" r="1.4" fill="currentColor" />
    </svg>
  );
}

function CardFace({ view }: { view: CardView }): ReactNode {
  return (
    <>
      <div className="amenity-card-front">
        <span className="amenity-card-icon">
          {view.icon === 'seesaw' ? (
            <SeesawIcon />
          ) : (
            <FontAwesomeIcon icon={view.icon} className="amenity-card-fa" aria-hidden="true" />
          )}
        </span>
        <span className="amenity-card-title">{view.title}</span>
      </div>

      {view.image ? (
        <span className="amenity-card-image">
          <VibesitesImage
            url={view.image}
            alt={view.alt}
            className="amenity-card-img"
            sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
          />
        </span>
      ) : null}
    </>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const items = useCollectionBinding<Amenity>('amenitieslist.items', {
    collection: 'amenities',
    defaultLimit: MAX_CARDS,
  });
  const gallery = useGallery() as GalleryPhoto[];
  // Photos categorized as "Other" are uncategorized junk (listing interiors,
  // stray uploads) and must never appear on a card back. Match/fill the card
  // photos only from the non-"Other" subset, and treat the "Other" URLs as a
  // blocklist so a stale generated/edited pick pointing at one is rejected too.
  const safeGallery = useMemo(() => excludeOtherPhotos(gallery), [gallery]);
  const blockedUrls = useMemo(() => otherPhotoUrls(gallery), [gallery]);

  useRevealStyles();
  const revealRef = useScrollReveal<HTMLDivElement>();

  // Fixed MAX_CARDS hook calls keep hook order stable regardless of how many
  // cards actually render.
  const slot0 = useSlotGroup('amenitieslist.card0', card0Def);
  const slot1 = useSlotGroup('amenitieslist.card1', card1Def);
  const slot2 = useSlotGroup('amenitieslist.card2', card2Def);
  const slot3 = useSlotGroup('amenitieslist.card3', card3Def);
  const slot4 = useSlotGroup('amenitieslist.card4', card4Def);
  const slot5 = useSlotGroup('amenitieslist.card5', card5Def);
  const slot6 = useSlotGroup('amenitieslist.card6', card6Def);
  const slot7 = useSlotGroup('amenitieslist.card7', card7Def);
  const slots = [slot0, slot1, slot2, slot3, slot4, slot5, slot6, slot7];

  // Show one card per available amenity, capped at MAX_CARDS, so small
  // properties never render bogus empty tiles.
  const count = Math.min(items.length, MAX_CARDS);

  const titles = slots.map((slot, i) => (slot.title || items[i]?.title || '').toString());
  // Photo selection is generator-first. Each image field has a defaultField of
  // photo_gallery[i].image, so an un-chosen slot resolves to the POSITIONAL gallery
  // photo (card i -> photo i), unrelated to the amenity. Treat that positional
  // default as "not chosen": the deterministic matcher fills the pre-generation
  // fallback, and once the content-aware generator (or an editor) writes a real
  // pick -- which differs from the positional default -- that pick is authoritative
  // and wins. (Edge case: a pick equal to the positional photo for that same card
  // gets re-derived by the matcher; harmless.)
  const chosenImages = slots.map((slot, i) => {
    const value = slot.image ? slot.image.toString() : '';
    const positionalDefault = (gallery[i]?.image || '').toString();
    // A pick that resolves to an "Other" photo is rejected (treated as un-chosen)
    // so the matcher re-fills it from the safe gallery below.
    if (blockedUrls.has(value)) return undefined;
    return value && value !== positionalDefault ? value : undefined;
  });

  // Deterministically match each un-chosen amenity to a distinct gallery photo.
  // Memoised so the assignment only recomputes when titles, real picks, or the
  // gallery actually change — never blank, never drifting.
  const galleryKey = safeGallery.map((p) => p.image || p.original_url || '').join('|');
  const photoAssignments = useMemo(
    () => assignPhotos(titles, safeGallery, chosenImages),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [titles.join(''), chosenImages.join(''), galleryKey],
  );

  const views: CardView[] = slots.map((slot, i) => {
    const title = titles[i];
    const icon = resolveAmenityIcon(title, (items[i]?.amenity_icon as string | null | undefined) ?? null);
    const chosen = chosenImages[i];
    const assigned = photoAssignments[i];
    // A real pick carries its own coherent alt text; the matcher's fallback carries
    // the matched photo's alt.
    const image = chosen || assigned?.image || '';
    const alt = chosen ? (slot.alt || title || 'Amenity') : (assigned?.alt || title || 'Amenity');
    return { title, icon, image, alt };
  });

  const cardClass = (view: CardView) =>
    `amenity-card${view.image ? ' amenity-card--has-image' : ''}`;

  return (
    <section className="pt-4 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {count > 0 ? (
          <div ref={revealRef} className="reveal-fade-up amenities-grid">
            {count > 0 && (
              <EditableSlotGroup groupPath="amenitieslist.card0" definition={card0Def} className={cardClass(views[0])}>
                <CardFace view={views[0]} />
              </EditableSlotGroup>
            )}
            {count > 1 && (
              <EditableSlotGroup groupPath="amenitieslist.card1" definition={card1Def} className={cardClass(views[1])}>
                <CardFace view={views[1]} />
              </EditableSlotGroup>
            )}
            {count > 2 && (
              <EditableSlotGroup groupPath="amenitieslist.card2" definition={card2Def} className={cardClass(views[2])}>
                <CardFace view={views[2]} />
              </EditableSlotGroup>
            )}
            {count > 3 && (
              <EditableSlotGroup groupPath="amenitieslist.card3" definition={card3Def} className={cardClass(views[3])}>
                <CardFace view={views[3]} />
              </EditableSlotGroup>
            )}
            {count > 4 && (
              <EditableSlotGroup groupPath="amenitieslist.card4" definition={card4Def} className={cardClass(views[4])}>
                <CardFace view={views[4]} />
              </EditableSlotGroup>
            )}
            {count > 5 && (
              <EditableSlotGroup groupPath="amenitieslist.card5" definition={card5Def} className={cardClass(views[5])}>
                <CardFace view={views[5]} />
              </EditableSlotGroup>
            )}
            {count > 6 && (
              <EditableSlotGroup groupPath="amenitieslist.card6" definition={card6Def} className={cardClass(views[6])}>
                <CardFace view={views[6]} />
              </EditableSlotGroup>
            )}
            {count > 7 && (
              <EditableSlotGroup groupPath="amenitieslist.card7" definition={card7Def} className={cardClass(views[7])}>
                <CardFace view={views[7]} />
              </EditableSlotGroup>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .amenities-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }
        /* Tablet (601–1024px): keep 3 smaller tiles per row rather than dropping
           to 2 oversized squares. Tighter gap shrinks the tiles a touch more. */
        @media (max-width: 1024px) {
          .amenities-grid { grid-template-columns: repeat(3, 1fr); gap: 1.25rem; }
        }
        /* Tablet and mobile: cap at 6 cards (two full rows of three on tablet,
           and less scrolling on mobile). */
        @media (max-width: 1024px) {
          .amenities-grid > .amenity-card:nth-child(n + 7) { display: none; }
        }
        @media (max-width: 600px) {
          .amenities-grid { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
        }
        @media (max-width: 420px) {
          .amenities-grid { grid-template-columns: 1fr; }
        }

        /* Square primary-color tile. Icon +
           title sit on the "front"; the photo layer is stacked on top at zero
           opacity and revealed on hover. */
        .amenity-card {
          position: relative;
          display: block;
          aspect-ratio: 1 / 1;
          background: var(--al-card-bg, var(--vibe-color-primary));
          overflow: hidden;
          outline: none;
        }

        .amenity-card-front {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1.5rem;
          padding: 1.5rem;
          text-align: center;
        }

        /* Icon shares the title's on-primary color so the icon and its
           accompanying text always render in the same coloring. */
        .amenity-card-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--al-card-text, var(--vibe-color-text-on-primary, #fff));
        }

        .amenity-card-fa {
          width: 3.25rem;
          height: 3.25rem;
        }
        @media (max-width: 1024px) {
          .amenity-card-fa { width: 2.75rem; height: 2.75rem; }
        }

        .amenity-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.05rem;
          font-weight: 600;
          line-height: 1.35;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: var(--al-card-text, var(--vibe-color-text-on-primary, #fff));
        }
        @media (max-width: 1024px) {
          .amenity-card-title { font-size: 0.95rem; }
          .amenity-card-front { gap: 1.15rem; padding: 1.25rem; }
        }

        /* Hover photo layer */
        .amenity-card-image {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1);
          z-index: 1;
        }
        .amenity-card--has-image:hover .amenity-card-image,
        .amenity-card--has-image:focus-within .amenity-card-image {
          opacity: 1;
        }
        .amenity-card-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .amenity-card--has-image { cursor: pointer; }

        @media (prefers-reduced-motion: reduce) {
          .amenity-card-image { transition: none; }
        }

        /* Mobile card overrides — placed AFTER the base rules so they win the
           cascade (equal specificity → later rule applies at <=600px). */
        @media (max-width: 600px) {
          /* Shorter, more landscape tiles on mobile. */
          .amenity-card { aspect-ratio: 3 / 2; }
          .amenity-card-front { gap: 0.6rem; padding: 0.85rem; }
          /* Slightly larger icon + title on mobile (may wrap to 2 lines). */
          .amenity-card-fa { width: 3rem; height: 3rem; }
          .amenity-card-title { font-size: 1.05rem; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {},
  slotGroups: {
    'amenitieslist.card0': card0Def,
    'amenitieslist.card1': card1Def,
    'amenitieslist.card2': card2Def,
    'amenitieslist.card3': card3Def,
    'amenitieslist.card4': card4Def,
    'amenitieslist.card5': card5Def,
    'amenitieslist.card6': card6Def,
    'amenitieslist.card7': card7Def,
  },
  collectionBindings: {
    'amenitieslist.items': {
      label: 'Amenities',
      collection: 'amenities',
      defaultLimit: 8,
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
